/*
  Holiday gift guide (KAN-715). Loaded by each gift-guide section; runs once.

  - Yes / No: writes the answer to <html data-gift-guide-path> and fades the
    newly shown content in. A #yes or #no link opens that view.
  - Track bottles: timed fades or uncovers, scroll-linked uncovering, or
    hover reveal (touch screens use scroll thresholds).
  - Preview themes: draggable, collapsible controls compare motion, button
    hover, and optional direct purchase on the fragrance and virtual gift cards.
  - Dice: rolls along the Figma prototype's paths. The long rolls follow the
    page as it scrolls; the short one plays the first time it is seen.
  - Add to cart: posts the card's form (with the name fields, in capitals) and
    refreshes the cart drawer.
*/
(() => {
  if (window.__giftGuideReady) return;
  window.__giftGuideReady = true;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const touchOnly = window.matchMedia('(hover: none)');

  const setPath = (path, { animate }) => {
    if (path !== 'yes' && path !== 'no') return;
    const changed = root.dataset.giftGuidePath !== path;
    root.dataset.giftGuidePath = path;

    document.querySelectorAll('[data-gg-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.ggChoice === path));
    });

    if (changed) document.dispatchEvent(new CustomEvent('gift-guide:path'));
    if (!animate || !changed || reduceMotion.matches) return;
    document.querySelectorAll('.gg-row, .gg-press').forEach((el) => {
      el.classList.remove('gg-fade-in');
      // Restart the animation on repeat answers.
      void el.offsetWidth;
      el.classList.add('gg-fade-in');
    });
  };

  const initQuestion = () => {
    const question = document.querySelector('[data-gg-question]');
    if (!question) return;
    // A link can open a view directly: /pages/gift-guide#yes or #no.
    const linked = window.location.hash.slice(1).toLowerCase();
    const start = linked === 'yes' || linked === 'no' ? linked : question.dataset.ggDefault;
    setPath(start || 'no', { animate: false });
  };

  const onFirstView = (elements, callback, options) => {
    if (!elements.length) return;
    if (!('IntersectionObserver' in window)) {
      elements.forEach(callback);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        callback(entry.target);
      });
    }, options);
    elements.forEach((el) => observer.observe(el));
  };

  /*
    Track bottles appear on hover (the Question section's "Bottles on the tracks
    appear" setting) or as each track scrolls into view. Touch screens always
    use scroll. <html data-gg-reveal> carries the mode for the CSS.
  */
  let stopReveal = () => {};

  const setReveal = (mode) => {
    window.GiftGuideMarkerLayout?.prepare();
    // Touch screens have no hover, so they uncover top to bottom instead.
    root.dataset.ggReveal = ['trigger', 'wipe', 'scroll'].includes(mode) ? mode : (touchOnly.matches ? 'wipe' : 'hover');
    stopReveal();
    const segments = Array.from(document.querySelectorAll('.gg [data-gg-seg]'));
    segments.forEach((seg) => seg.classList.remove('is-revealed'));
    if (reduceMotion.matches || (!touchOnly.matches && root.dataset.ggReveal === 'hover')) {
      if (reduceMotion.matches) segments.forEach((seg) => seg.classList.add('is-revealed'));
      let frame = 0;
      let disposed = false;
      const refresh = () => {
        if (disposed || frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          window.GiftGuideMarkerLayout?.layout();
        });
      };
      const sizes = 'ResizeObserver' in window ? new ResizeObserver(refresh) : null;
      sizes?.observe(document.body);
      window.addEventListener('resize', refresh);
      document.addEventListener('gift-guide:path', refresh);
      document.fonts?.ready.then(refresh);
      stopReveal = () => {
        disposed = true;
        cancelAnimationFrame(frame);
        sizes?.disconnect();
        window.removeEventListener('resize', refresh);
        document.removeEventListener('gift-guide:path', refresh);
      };
      return;
    }
    const timed = root.dataset.ggReveal !== 'scroll';
    const wipe = root.dataset.ggReveal === 'wipe';
    const motions = new WeakMap();
    const scrollProgress = new WeakMap();
    const duration = 1200;
    const reverseGap = 48;
    let tracks = [];
    let frame = 0;
    let layoutFrame = 0;
    let lastTime = 0;
    let disposed = false;
    let scrollDirection = 1;
    let turningPoint = window.scrollY;
    const clamp = (value) => Math.min(1, Math.max(0, value));
    const targetAt = (reveal, scroll) => clamp((scroll - reveal.threshold) / Math.min(240, window.innerHeight * 0.3));
    const clipAt = (time) => {
      const progress = clamp(time / duration);
      const outset = 2 - progress * 4;
      return `inset(-2px -2px calc(${(1 - progress) * 100}% ${outset < 0 ? '-' : '+'} ${Math.abs(outset)}px) -2px) border-box`;
    };
    const motionTime = (motion, now) => {
      if (!motion) return 0;
      const elapsed = Math.max(0, now - motion.startTime);
      const target = motion.visible ? duration : 0;
      return motion.from + (target - motion.from) * clamp(elapsed / duration);
    };
    const applyMotion = (animation, motion) => {
      const now = document.timeline.currentTime;
      animation.pause();
      animation.effect.setKeyframes(wipe ? [
        { clipPath: clipAt(motion.from) },
        { clipPath: clipAt(motion.visible ? duration : 0) },
      ] : [
        { opacity: motion.from / duration },
        { opacity: motion.visible ? 1 : 0 },
      ]);
      animation.playbackRate = 1;
      animation.currentTime = Math.min(duration, now - motion.startTime);
      if (now - motion.startTime >= duration) animation.finish();
      else {
        animation.play();
        animation.startTime = motion.startTime;
      }
    };
    const disposeTrack = (track) => {
      track.animations.forEach(({ animation }) => animation.cancel());
      track.outlines.forEach((outline) => outline.remove());
      track.restore.forEach((restore) => restore());
    };
    const orderRoute = () => {
      const box = (element) => {
        const rect = element.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2,
          top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right };
      };
      const distanceAlong = (points, point) => {
        let walked = 0;
        let closest = { error: Infinity, distance: 0 };
        points.slice(1).forEach((end, index) => {
          const start = points[index];
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const length = Math.hypot(dx, dy);
          if (!length) return;
          const t = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / (length * length));
          const error = Math.hypot(point.x - start.x - t * dx, point.y - start.y - t * dy);
          if (error < closest.error) closest = { error, distance: walked + t * length };
          walked += length;
        });
        return closest.distance;
      };
      tracks.forEach((track) => track.reveals.forEach((reveal) => {
        reveal.parents = [];
        reveal.children = [];
      }));
      const trace = (track, points) => (track?.reveals || []).map((reveal) => ({
        reveal, distance: distanceAlong(points, box(reveal.mark)),
      }));
      const sequence = (entries, parents, start, span) => {
        entries.sort((a, b) => a.distance - b.distance);
        const length = Math.max(1, ...entries.map((entry) => entry.distance));
        let wave = [];
        let previous = parents;
        let distance = -Infinity;
        entries.forEach((entry) => {
          if (entry.distance - distance > 1) {
            if (wave.length) previous = wave;
            wave = [];
            distance = entry.distance;
          }
          const { reveal } = entry;
          reveal.threshold = Math.max(start + entry.distance / length * span,
            ...previous.map((parent) => parent.threshold + 1));
          reveal.parents = previous;
          previous.forEach((parent) => parent.children.push(reveal));
          wave.push(reveal);
        });
        return wave.length ? wave : parents;
      };
      const stem = tracks.find(({ segment }) => segment.matches('.gg-question__start .gg-stem'));
      const question = tracks.find(({ segment }) => segment.matches('.gg-question__track'));
      let parents = [];
      if (stem && question) {
        const origin = box(stem.segment);
        const bar = box(question.segment);
        const points = [{ x: origin.x, y: origin.top }, { x: origin.x, y: bar.y }];
        const entries = trace(stem, points);
        question.reveals.forEach((reveal) => {
          const mark = box(reveal.mark);
          const end = { x: mark.x < origin.x ? bar.left : bar.right, y: bar.y };
          entries.push({ reveal, distance: distanceAlong([...points, end], mark) });
        });
        sequence(entries, [], stem.start, Math.min(240, window.innerHeight * 0.3));
        const yes = root.dataset.giftGuidePath === 'yes';
        const branch = entries.filter(({ reveal }) => reveal.mark.closest('.gg-question__track')
          && (box(reveal.mark).x < origin.x) === yes);
        parents = branch.length ? [branch.at(-1).reveal] : stem.reveals;
      } else if (stem) {
        parents = sequence(trace(stem, [{ x: box(stem.segment).x, y: box(stem.segment).top },
          { x: box(stem.segment).x, y: box(stem.segment).bottom }]), [], stem.start, 0);
      }
      const boards = new Set(tracks.map(({ segment }) => segment.closest('.gg-board')).filter(Boolean));
      boards.forEach((board) => {
        const parts = tracks.filter(({ segment }) => segment.closest('.gg-board') === board);
        const stem = parts.find(({ segment }) => segment.matches('.gg-stem'));
        const bar = parts.find(({ segment }) => segment.matches('.gg-bar'));
        const drops = parts.filter(({ segment }) => segment.matches('.gg-drop, .gg-leg'));
        if (!bar) {
          const entries = parts.flatMap((track) => trace(track, [
            { x: box(track.segment).x, y: box(track.segment).top },
            { x: box(track.segment).x, y: box(track.segment).bottom },
          ]));
          parents = sequence(entries, parents, Math.max(parts[0].start, ...parents.map((p) => p.threshold + 24)), 80);
          return;
        }
        const bounds = box(bar.segment);
        // Follow the ink: enter at the stem, turn onto the bar, then turn down
        // at each card junction. A drop can be reached before a later bar mark.
        const entry = stem ? box(stem.segment) : { x: bounds.left, top: bounds.y };
        const trunk = [{ x: entry.x, y: entry.top }, { x: entry.x, y: bounds.y }];
        const entries = trace(stem, trunk);
        bar.reveals.forEach((reveal) => {
          const mark = box(reveal.mark);
          const end = { x: mark.x < entry.x ? bounds.left : bounds.right, y: bounds.y };
          entries.push({ reveal, distance: distanceAlong([...trunk, end], mark) });
        });
        drops.forEach((drop) => {
          const end = box(drop.segment);
          entries.push(...trace(drop, [...trunk, { x: end.x, y: bounds.y }, { x: end.x, y: end.bottom }]));
        });
        const start = Math.max((stem || bar).start, ...parents.map((parent) => parent.threshold + 24));
        parents = sequence(entries, parents, start, Math.min(360, window.innerHeight * 0.45));
      });
    };
    // The first pass after the page opens (scrolled down after Back or a reload)
    // shows the tracks already passed as finished, instead of drawing them again.
    let settled = false;
    const follow = (now) => {
      frame = 0;
      if (disposed) return;
      // Only when the page opened scrolled down: a visit from the top still draws them in.
      const instant = !settled && tracks.length > 0 && window.scrollY > window.innerHeight * 0.5;
      if (tracks.length) settled = true;
      const elapsed = Math.min(64, Math.max(0, now - lastTime));
      lastTime = now;
      const blend = 1 - Math.exp(-elapsed / 90);
      const scroll = window.scrollY;
      if (timed) {
        turningPoint = scrollDirection > 0 ? Math.max(turningPoint, scroll) : Math.min(turningPoint, scroll);
        if ((scroll - turningPoint) * scrollDirection <= -reverseGap) {
          scrollDirection *= -1;
          turningPoint = scroll;
        }
        const changes = [];
        tracks.forEach((track) => track.reveals.forEach((reveal) => {
          const threshold = reveal.threshold;
          const motion = motions.get(reveal.mark);
          // Cover while the artwork is still in view, rather than waiting until
          // it reaches the bottom edge/preview bar. Small scroll jitters do not
          // change direction, and reversing still follows the traced path.
          const visible = scrollDirection > 0
            ? scroll >= threshold || Boolean(motion?.visible)
            : scroll > threshold + window.innerHeight * 0.3 && Boolean(motion?.visible);
          if (visible === Boolean(motion?.visible)) return;
          changes.push({ reveal, visible, threshold, motion });
        }));
        changes.sort((a, b) => a.visible === b.visible
          ? (a.visible ? a.threshold - b.threshold : b.threshold - a.threshold) : Number(a.visible) - Number(b.visible));
        const now = document.timeline.currentTime;
        changes.forEach(({ reveal, visible, motion }) => {
          const from = motionTime(motion, now);
          // A direction change in flight starts from the current drawing,
          // without a delay that would briefly move it the wrong way.
          const atStart = visible ? from === 0 : from === duration;
          const preceding = (visible ? reveal.parents : reveal.children).map(({ mark }) => motions.get(mark))
            .filter((state) => state && state.visible === visible);
          const startTime = atStart ? Math.max(now, ...preceding.map((state) => state.startTime + 80)) : now;
          const next = instant && visible && !motion
            ? { visible, from: duration, startTime: now - duration }
            : { visible, from, startTime };
          motions.set(reveal.mark, next);
          reveal.animations.forEach((animation) => applyMotion(animation, next));
        });
        return;
      }
      let moving = false;
      tracks.forEach((track) => {
        track.reveals.forEach((reveal) => {
          const target = targetAt(reveal, scroll);
          const difference = target - reveal.progress;
          const next = instant || Math.abs(difference) < 0.0001 ? target : reveal.progress + difference * blend;
          if (next !== reveal.progress) {
            reveal.progress = next;
            scrollProgress.set(reveal.mark, next);
            reveal.animations.forEach((animation) => { animation.currentTime = next * 1000; });
          }
          if (next !== target) moving = true;
        });
      });
      if (moving) frame = requestAnimationFrame(follow);
    };
    const queue = () => {
      if (disposed || frame) return;
      lastTime = performance.now();
      frame = requestAnimationFrame(follow);
    };
    const layout = () => {
      layoutFrame = 0;
      if (disposed) return;
      window.GiftGuideMarkerLayout?.layout();
      const previous = new Map(tracks.map((track) => [track.segment, track]));
      const scroll = window.scrollY;
      tracks = segments.filter((seg) => seg.getClientRects().length && getComputedStyle(seg).visibility !== 'hidden').map((seg) => {
        seg.classList.add('is-revealed');
        const box = seg.getBoundingClientRect();
        const vertical = seg.matches('.gg-stem, .gg-drop, .gg-leg');
        const stem = seg.closest('[data-gg-stem]')?.dataset.ggStem;
        const reverse = !vertical && (stem === 'right' || (stem === 'follow' && root.dataset.giftGuidePath !== 'yes'));
        const groups = Array.from(seg.querySelectorAll('.gg-marks')).filter((group) => group.getClientRects().length).map((group) => {
          const bounds = group.getBoundingClientRect();
          return { group, length: (vertical ? bounds.height : bounds.width) + 4,
            position: vertical ? bounds.top : reverse ? -bounds.right : bounds.left };
        }).sort((a, b) => a.position - b.position);
        const artworkLength = groups.reduce((sum, group) => sum + group.length, 0);
        const distance = Math.min(Math.max(240, artworkLength * 3), Math.max(240, window.innerHeight * 0.7));
        const start = box.top + scroll - window.innerHeight * 0.85;
        const old = previous.get(seg);
        const unchanged = old && old.vertical === vertical && old.reverse === reverse && old.groups.length === groups.length
          && groups.every((item, i) => item.group === old.groups[i].group && item.length === old.groups[i].length);
        previous.delete(seg);
        if (unchanged) return { ...old, start, distance };
        if (old) disposeTrack(old);
        const track = { segment: seg, start, distance, groups, vertical, reverse,
          animations: [], outlines: [], restore: [], reveals: [] };
        const marks = groups.flatMap(({ group }) => Array.from(group.querySelectorAll('.gg-mark')).filter((mark) => mark.getClientRects().length && getComputedStyle(mark).visibility !== 'hidden').map((mark) => {
          const bounds = mark.getBoundingClientRect();
          return { mark, position: vertical ? bounds.top : reverse ? -bounds.right : bounds.left };
        })).sort((a, b) => a.position - b.position);
        const span = 1;
        const animate = (element, keyframes, reveal) => {
          const animation = element.animate(keyframes, { duration: timed ? duration : 1000,
            fill: 'both', easing: 'linear' });
          animation.pause();
          // Leave not-yet-started and completed effects parked. Only drawings
          // whose appearance is changing need timeline writes on each frame.
          const start = 0;
          const end = timed ? duration : 1000;
          const time = 0;
          animation.currentTime = time;
          track.animations.push({ animation, start, end, time });
          reveal.animations.push(animation);
          if (timed && motions.has(reveal.mark)) applyMotion(animation, motions.get(reveal.mark));
        };
        marks.forEach(({ mark }) => {
          const gate = 0;
          const reveal = { mark, gate, animations: [] };
          track.reveals.push(reveal);
          if (timed) {
            const keyframes = wipe ? [
              { offset: 0, clipPath: clipAt(0) },
              { offset: 1, clipPath: clipAt(duration) },
            ] : [{ offset: 0, opacity: 0 }, { offset: 1, opacity: 1 }];
            animate(mark, keyframes, reveal);
            return;
          }
          const leading = timed ? 0 : gate;
          const at = (fraction) => leading + span * fraction;
          if (mark.classList.contains('gg-mark--figma')) {
            animate(mark, [
              { offset: 0, clipPath: clipAt(0) }, { offset: leading, clipPath: clipAt(0) },
              { offset: at(1), clipPath: clipAt(duration) }, { offset: 1, clipPath: clipAt(duration) },
            ], reveal);
            return;
          }
          mark.querySelectorAll('path, rect').forEach((shape) => {
            const color = shape.getAttribute('fill');
            const solid = color && color !== 'none';
            const outline = solid ? shape.cloneNode(false) : shape;
            if (!solid) {
              const attributes = ['pathLength', 'stroke-dasharray', 'stroke-linecap'];
              const original = attributes.map((name) => [name, shape.getAttribute(name)]);
              track.restore.push(() => original.forEach(([name, value]) => {
                if (value === null) shape.removeAttribute(name);
                else shape.setAttribute(name, value);
              }));
            }
            outline.setAttribute('pathLength', '1');
            outline.setAttribute('stroke-dasharray', '1 1');
            outline.setAttribute('stroke-linecap', 'round');
            if (solid) {
              outline.dataset.ggDraw = '';
              outline.setAttribute('fill', 'none');
              outline.setAttribute('stroke', color);
              outline.setAttribute('stroke-width', mark.classList.contains('gg-mark--match') ? '0.75' : '1.5');
              mark.appendChild(outline);
              track.outlines.push(outline);
            }
            animate(outline, [
              { offset: 0, strokeDashoffset: '1' }, { offset: leading, strokeDashoffset: '1' },
              { offset: at(solid ? 0.72 : 1), strokeDashoffset: '0' }, { offset: 1, strokeDashoffset: '0' },
            ], reveal);
            if (!solid) {
              animate(shape, [
                { offset: 0, opacity: 0 }, { offset: leading, opacity: 0 },
                { offset: at(0.04), opacity: 1 }, { offset: 1, opacity: 1 },
              ], reveal);
              return;
            }
            // Fade the solid color in while the final contour is being drawn.
            // End on the untouched source shape, with no added stroke weight.
            const fillStart = at(0.55);
            animate(shape, [
              { offset: 0, opacity: 0 }, { offset: fillStart, opacity: 0, easing: 'ease-in-out' },
              { offset: at(1), opacity: 1 }, { offset: 1, opacity: 1 },
            ], reveal);
            animate(outline, [
              { offset: 0, opacity: 0 }, { offset: leading, opacity: 0 },
              { offset: at(0.04), opacity: 1 }, { offset: at(0.8), opacity: 1 },
              { offset: at(1), opacity: 0 }, { offset: 1, opacity: 0 },
            ], reveal);
          });
        });
        return track;
      });
      previous.forEach(disposeTrack);
      orderRoute();
      if (!timed) tracks.forEach((track) => track.reveals.forEach((reveal) => {
        reveal.progress = scrollProgress.get(reveal.mark) ?? targetAt(reveal, scroll);
        scrollProgress.set(reveal.mark, reveal.progress);
        reveal.animations.forEach((animation) => { animation.currentTime = reveal.progress * 1000; });
      }));
      queue();
    };
    const queueLayout = () => { if (!disposed && !layoutFrame) layoutFrame = requestAnimationFrame(layout); };
    const pathLayout = () => {
      tracks = tracks.filter((track) => {
        if (track.segment.closest('[data-gg-question]')) return true;
        track.reveals.forEach(({ mark }) => { motions.delete(mark); scrollProgress.delete(mark); });
        disposeTrack(track);
        return false;
      });
      queueLayout();
    };
    const sizes = 'ResizeObserver' in window ? new ResizeObserver(queueLayout) : null;
    if (sizes) sizes.observe(document.body);
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queueLayout);
    document.addEventListener('gift-guide:path', pathLayout);
    if (document.fonts) document.fonts.ready.then(queueLayout);
    layout();
    stopReveal = () => {
      disposed = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(layoutFrame);
      tracks.forEach(disposeTrack);
      if (sizes) sizes.disconnect();
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queueLayout);
      document.removeEventListener('gift-guide:path', pathLayout);
    };
  };

  // Preview themes and the theme editor only (the purchase comparison).
  const isPreview = () => Boolean(window.Shopify && (window.Shopify.designMode
    || (window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main')));

  // Hannah, 5 Oct (KAN-811): bottles uncover top to bottom as each track scrolls in.
  const initReveal = () => {
    const question = document.querySelector('[data-gg-question]');
    setReveal((question && question.dataset.ggReveal) || 'wipe');
  };

  const initVariantPickers = () => {
    document.querySelectorAll('[data-gg-variant]').forEach((select) => {
      if (select.dataset.enhanced) return;
      select.dataset.enhanced = 'true';
      const label = select.labels[0];
      label.id = `${select.id}-label`;
      const wrapper = document.createElement('div');
      wrapper.className = 'gg-variant';
      wrapper.innerHTML = `<button type="button" class="gg-variant__trigger" data-gg-variant-trigger aria-haspopup="listbox" aria-expanded="false"><span class="gg-variant__value"></span><svg class="gg-variant__chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m3 6 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button><div class="gg-variant__menu" role="listbox" hidden></div>`;
      const trigger = wrapper.querySelector('button');
      const value = wrapper.querySelector('.gg-variant__value');
      const menu = wrapper.querySelector('[role="listbox"]');
      trigger.id = `${select.id}-trigger`;
      value.id = `${select.id}-value`;
      menu.id = `${select.id}-menu`;
      trigger.setAttribute('aria-labelledby', `${label.id} ${value.id}`);
      trigger.setAttribute('aria-controls', menu.id);
      menu.setAttribute('aria-labelledby', label.id);
      label.htmlFor = trigger.id;
      const options = Array.from(select.options).map((option) => {
        const item = document.createElement('div');
        item.className = 'gg-variant__option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-disabled', String(option.disabled));
        item.tabIndex = -1;
        item.dataset.value = option.value;
        item.textContent = option.textContent;
        menu.appendChild(item);
        return item;
      });
      const close = (restoreFocus = false) => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) trigger.focus({ preventScroll: true });
      };
      const sync = () => {
        value.textContent = select.selectedOptions[0]?.textContent || '';
        trigger.disabled = select.disabled;
        options.forEach((item) => item.setAttribute('aria-selected', String(item.dataset.value === select.value)));
        if (trigger.disabled) close();
      };
      const available = () => options.filter((item) => item.getAttribute('aria-disabled') !== 'true');
      const open = () => {
        if (trigger.disabled) return;
        const box = trigger.getBoundingClientRect();
        const previewBar = document.querySelector('#PBarNextFrame')?.getBoundingClientRect();
        const bottom = previewBar && previewBar.height && previewBar.top > 0 ? Math.min(innerHeight, previewBar.top) : innerHeight;
        let below = bottom - box.bottom - 20;
        let above = box.top - 20;
        const panel = document.querySelector('preview-states-panel')?.getBoundingClientRect();
        if (panel && panel.left < box.right && panel.right > box.left) {
          if (panel.top >= box.bottom) below = Math.min(below, panel.top - box.bottom - 16);
          if (panel.bottom <= box.top) above = Math.min(above, box.top - panel.bottom - 16);
        }
        const upwards = below < 280 && above > below;
        wrapper.toggleAttribute('data-open-above', upwards);
        wrapper.style.setProperty('--gg-menu-height', `${Math.min(280, Math.max(80, upwards ? above : below))}px`);
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        const selected = available().find((item) => item.dataset.value === select.value) || available()[0];
        selected?.focus({ preventScroll: true });
        selected?.scrollIntoView({ block: 'nearest' });
      };
      const choose = (item) => {
        if (!item || item.getAttribute('aria-disabled') === 'true') return;
        select.value = item.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close(true);
      };
      trigger.addEventListener('click', () => menu.hidden ? open() : close(true));
      menu.addEventListener('click', (event) => choose(event.target.closest('[role="option"]')));
      let typed = '';
      let lastTyped = 0;
      wrapper.addEventListener('keydown', (event) => {
        const items = available();
        const index = items.indexOf(document.activeElement);
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
        if (event.key === 'Tab') { close(true); return; }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (menu.hidden) { open(); return; }
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : Math.max(0, Math.min(items.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
          items[next]?.focus({ preventScroll: true });
          items[next]?.scrollIntoView({ block: 'nearest' });
        } else if ((event.key === 'Enter' || event.key === ' ') && !menu.hidden) {
          event.preventDefault();
          choose(items[index]);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          if (menu.hidden) open();
          const now = performance.now();
          typed = now - lastTyped > 700 ? event.key : typed + event.key;
          lastTyped = now;
          const match = items.find((item) => item.textContent.trim().toLowerCase().startsWith(typed.toLowerCase()));
          match?.focus({ preventScroll: true });
          match?.scrollIntoView({ block: 'nearest' });
        }
      });
      wrapper.addEventListener('focusout', (event) => { if (!wrapper.contains(event.relatedTarget)) close(); });
      document.addEventListener('pointerdown', (event) => { if (!wrapper.contains(event.target)) close(); });
      window.addEventListener('resize', () => close());
      select.addEventListener('change', sync);
      select.addEventListener('gg:sync', sync);
      select.hidden = true;
      select.after(wrapper);
      sync();
    });
  };

  const setPurchase = (mode) => {
    const quick = isPreview() && mode === 'cart';
    root.dataset.ggPurchase = quick ? 'cart' : 'shop';
    document.querySelectorAll('[data-gg-quick-buy]').forEach((form) => {
      form.hidden = !quick;
      form.closest('.gg-card').querySelector('[data-gg-shop]').hidden = quick;
    });
  };

  const initPreviewStates = () => {
    if (!isPreview() || !document.querySelector('[data-gg-question]') || !customElements.get('preview-states-panel')) return;
    setPurchase(new URLSearchParams(location.search).get('gg_purchase'));
    const params = new URLSearchParams(location.search);
    if (params.get('gg_dicepath') === 'figma') root.dataset.ggDicePath = 'figma';
    if (params.has('gg_dicepath')) document.dispatchEvent(new CustomEvent('gift-guide:path'));
    if (document.querySelector('preview-states-panel')) return;
    const menu = document.createElement('preview-states-panel');
    menu.configure({
      storageKey: `gift-guide-preview:${window.Shopify.theme?.id}:${location.pathname}`,
      groups: [
        { id: 'dicepath', label: 'Dice while rolling', value: root.dataset.ggDicePath === 'figma' ? 'figma' : 'clear', options: [
          { value: 'clear', label: 'Keep 5px clear of the cards' },
          { value: 'figma', label: 'Figma path (passes over cards)' },
        ] },
        { id: 'purchase', label: 'Travel Size + Virtual Gift Card', value: root.dataset.ggPurchase, options: [
          { value: 'shop', label: 'Shop Now → product page' },
          { value: 'cart', label: 'Choose fragrance/amount + Add to cart' },
        ] },
      ],
    });
    menu.addEventListener('preview-state-change', ({ detail }) => {
      if (detail.group === 'purchase') setPurchase(detail.value);
      if (detail.group === 'dicepath') {
        root.dataset.ggDicePath = detail.value;
        document.dispatchEvent(new CustomEvent('gift-guide:path'));
      }
      const url = new URL(location.href);
      url.searchParams.set(`gg_${detail.group}`, detail.value);
      history.replaceState(null, '', url);
    });
    document.body.appendChild(menu);
  };

  /*
    Dice paths, copied from the Figma prototype's keyframes (get_motion_context,
    2026-09-22). translate: [% of the timeline, x px, y px]; rotate: [% of the
    timeline, radians]. Every rotate step uses the Figma's cubic-bezier(0.5, 0,
    0.5, 1). `window` is the stretch of the timeline where the dice moves.
    The dice is drawn at its final spot, so the path is played relative to it.
  */
  const DICE_PATHS = {
    desktop_no: {
      anchor: { selector: '[id$="__first_row_no"] .gg-card', pick: 'first', dy: -37 },
      rest: 'on-card',
      duration: 10003, window: [19.973, 60.451], trigger: 'scroll',
      translate: [[0, 118, -618], [19.973, 118, -618], [26.769, 49.593, -404.407], [30.032, 122.612, -349.388], [33.389, 50.635, -210.365], [40.144, 124.879, 48.879], [44.176, -158.605, -86.605], [45.578, -178.515, -66.515], [46.94, -170.515, -44.515], [49.596, -57.515, -86.515], [56.999, 119.775, 491.775], [60.451, 97.101, 646.102], [100, 97.101, 646.102]],
      rotate: [[0, 0], [19.973, 0], [26.769, -0.619], [30.032, -1.182], [33.389, -2.144], [40.144, -2.666], [44.176, -3.16], [45.578, -3.319], [56.999, 1.821], [60.451, 2.357], [100, 2.357]],
    },
    desktop_yes: {
      anchor: { selector: '[id$="__first_row_yes"] .gg-card', pick: 'first', dy: 85 },
      rest: 'on-card',
      // The roll starts with the die just touching this card's right edge.
      edge: { selector: '[id$="__first_row_yes"] .gg-card', pick: 'first' },
      duration: 10069, window: [19.733, 60.193], trigger: 'scroll',
      translate: [[0, 0, 0], [19.733, 0, 0], [22.878, 117, -124], [25.898, 406.641, -20.359], [32.112, -9.32, 270.68], [33.404, 23.918, 334.919], [38.375, 407.868, 283.868], [44.564, 212.571, 539.571], [50.828, 408.332, 810.333], [53.848, 228.968, 909.968], [56.968, 413.943, 1076.943], [60.087, 382.687, 1185.687], [60.193, 380.688, 1244.687], [100, 380.688, 1244.687]],
      rotate: [[0, 0], [19.733, 0], [25.898, 0.136], [32.112, -0.549], [33.404, 0.257], [38.375, -0.274], [44.564, -0.642], [50.828, -1.288], [53.848, -1.828], [56.968, -2.62], [60.087, -2.358], [100, -2.358]],
    },
    phone_no: {
      anchor: { selector: '[id$="__first_row_no"] .gg-card', pick: 'last', dy: 14 },
      rest: 'on-bar',
      duration: 5540, window: [0, 81.138], trigger: 'scroll',
      translate: [[0, 0, -3], [12.897, -55.474, 57.526], [25.69, 8.003, 118.003], [38.638, -56.686, 207.314], [44.997, 7.679, 273.68], [51.355, -30.166, 327.834], [57.996, -59.413, 270.587], [64.38, -80.518, 333.482], [70.687, -113.797, 287.203], [73.467, -119.821, 310.179], [75.835, -107.227, 329.772], [78.487, -118.548, 329.452], [81.138, -123.505, 332.496], [100, -123.505, 332.496]],
      rotate: [[0, 0], [12.897, -0.2], [25.69, 0.41], [38.638, -0.435], [44.997, 0.386], [51.355, 0.216], [57.996, 0.457], [64.38, 0.78], [70.687, 1.563], [73.467, 0.007], [75.835, 1.178], [78.487, 0.37], [81.138, 0.778], [100, 0.778]],
    },
    phone_yes: {
      anchor: { selector: '.gg-question__bar', pick: 'first', dy: 39 },
      rest: 'on-bar',
      // Starts beside the NO box, as drawn: on the narrowest phones the die is
      // drawn smaller rather than moved below the YES / NO boxes.
      startWithin: 40,
      duration: 10012, window: [0, 50.11], trigger: 'scroll',
      translate: [[0, 0, 0], [4.951, 21, 56], [7.792, -116, 99], [10.479, -166.572, 80.428], [11.295, -160.13, 62.871], [14.065, -120.779, 64.221], [17.512, 22.817, 138.816], [20.437, -6.975, 205.025], [23.152, 26.422, 237.422], [25.965, -8.624, 282.375], [28.89, 23.436, 315.436], [37.535, -7.564, 457.436], [41.065, 23.827, 521.827], [44.54, -63.195, 572.805], [47.296, -91.195, 532.805], [50.11, -110.527, 578.473], [100, -110.527, 578.473]],
      rotate: [[0, 0], [7.792, -1.575], [10.479, -1.809], [11.295, -1.842], [14.08, -2.906], [17.512, 0.396], [20.437, 0.175], [23.152, -0.85], [25.965, -2.105], [28.89, -1.126], [41.065, 0.479], [44.54, -0.118], [47.296, -0.206], [50.11, -0.784], [100, -0.784]],
    },
    phone_nudge: {
      rest: 'by-leg',
      /*
        The Figma plays this roll in time once the board is in view; a visitor
        scrolling down passed it before it got going (Cooper, 13 Oct), so it is
        tied to scroll like the others: it rolls while its rest spot rises from
        the lower part of the screen (above the floating buttons) to just
        above the middle. It keeps clear of the bar, the
        leg and the UNDER $50 badge too, as it rolls inside them.
      */
      travel: [0.8, 0.45],
      avoidTrack: true,
      duration: 5540, window: [0, 78.509], trigger: 'scroll',
      translate: [[0, 0, 0], [12.979, 141.335, -51.665], [17.998, 164.388, -37.187], [25.778, 140.712, 0.711], [31.645, -7.403, -53.403], [36.068, -34.136, 0.864], [45.094, -114.764, -28.764], [51.412, -56.74, -58.74], [57.713, 53.862, 3.862], [64.392, 127.568, -49.433], [67.75, 164.796, -25.204], [70.62, 149.813, 3.813], [73.472, 166.49, 4.49], [78.509, 169.78, 5.78], [100, 169.78, 5.78]],
      rotate: [[0, -0.641], [12.979, -0.271], [17.998, -0.837], [25.778, -2.213], [31.645, -1.472], [36.068, -0.687], [45.094, -0.43], [51.412, 0.081], [57.713, -0.31], [64.392, -0.61], [67.75, -0.905], [70.62, -1.877], [73.472, -1.42], [75.873, -1.741], [78.509, -1.568], [100, -1.568]],
    },
  };
  const EASE = 'cubic-bezier(0.5, 0, 0.5, 1)';

  // The y of EASE at a given x (0-1), for sampling the rotation between keys.
  const ease = (x) => {
    let lo = 0;
    let hi = 1;
    let t = x;
    for (let i = 0; i < 24; i += 1) {
      const at = 1.5 * (1 - t) * (1 - t) * t + 1.5 * (1 - t) * t * t + t * t * t;
      if (at < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return 3 * (1 - t) * t * t + t * t * t;
  };

  // The value of a keyframe track at a point of the timeline (in %).
  const trackAt = (keys, pct, easedFrom) => {
    if (pct <= keys[0][0]) return keys[0].slice(1);
    for (let i = 1; i < keys.length; i += 1) {
      const [p1, ...to] = keys[i];
      if (pct > p1) continue;
      const [p0, ...from] = keys[i - 1];
      let u = p1 === p0 ? 1 : (pct - p0) / (p1 - p0);
      if (easedFrom !== undefined && p0 >= easedFrom) u = ease(u);
      return from.map((v, j) => v + (to[j] - v) * u);
    }
    return keys[keys.length - 1].slice(1);
  };

  const buildDice = (dice) => {
    const path = DICE_PATHS[dice.dataset.ggMotion];
    const svg = dice.querySelector('svg');
    if (!path || !svg || !dice.animate) return null;
    const [, endX, endY] = path.translate[path.translate.length - 1];
    const toTime = (pct) => pct / 100;
    const [from, to] = path.window;

    if (path.trigger === 'view') {
      // Played in time, as in the Figma prototype.
      const moves = dice.animate(
        path.translate.map(([pct, x, y]) => ({ offset: toTime(pct), transform: `translate(${x - endX}px, ${y - endY}px)` })),
        { duration: path.duration, fill: 'both' }
      );
      const turns = svg.animate(
        path.rotate.map(([pct, rad], i) => ({
          offset: toTime(pct),
          transform: `rotate(${rad}rad)`,
          easing: i < path.rotate.length - 1 && pct >= from ? EASE : 'linear',
        })),
        { duration: path.duration, fill: 'both' }
      );
      moves.pause();
      turns.pause();
      moves.currentTime = turns.currentTime = toTime(from) * path.duration;
      return { dice, path, moves, turns };
    }

    /*
      Scroll-linked rolls. The Figma times its path in seconds, so some stretches
      cover hundreds of pixels in a fraction of a second; tied to scroll, those
      stretches made the die leap. Here the die travels its path at an even
      distance per pixel scrolled (it keeps every point, turn and its rest spot).
      Where the browser supports scroll timelines the roll runs on the
      compositor, in step with the page, rather than a frame behind it.
    */
    const points = path.translate.filter(([pct]) => pct >= from && pct <= to);
    const figmaLift = points[0][2] - endY;

    // Samples of the path at even steps along its length, for a vertical stretch.
    const sample = (scaleY, scaleX = 1) => {
      const lengths = [0];
      for (let i = 1; i < points.length; i += 1) {
        const [, x0, y0] = points[i - 1];
        const [, x1, y1] = points[i];
        lengths.push(lengths[i - 1] + Math.hypot((x1 - x0) * scaleX, (y1 - y0) * scaleY));
      }
      const total = lengths[lengths.length - 1] || 1;
      const steps = 400;
      return Array.from({ length: steps + 1 }, (_, n) => {
        const along = (n / steps) * total;
        let i = 1;
        while (i < points.length - 1 && lengths[i] < along) i += 1;
        const span = lengths[i] - lengths[i - 1];
        const u = span ? (along - lengths[i - 1]) / span : 1;
        const pct = points[i - 1][0] + (points[i][0] - points[i - 1][0]) * u;
        const [x, y] = trackAt(path.translate, pct);
        const [rad] = trackAt(path.rotate, pct, from);
        return { p: n / steps, x: (x - endX) * scaleX, y: (y - endY) * scaleY, rad };
      });
    };

    const timeline = typeof window.ScrollTimeline === 'function'
      ? new window.ScrollTimeline({ source: document.documentElement, axis: 'block' })
      : null;
    const options = timeline ? { timeline, fill: 'both' } : { duration: 1000, fill: 'both' };
    // One transform on the wrapper (move, then turn about the die's centre):
    // Chrome cannot run an animation on the SVG itself off the main thread.
    dice.classList.add('is-rolling');
    const moves = dice.animate([{ transform: 'none' }, { transform: 'none' }], options);
    if (!timeline) moves.pause();
    return { dice, path, moves, timeline, sample, figmaLift, range: null };
  };

  /*
    The painted die inside its box (the Figma's DICE frame, see
    snippets/gift-guide-dice.liquid): turned square-on, the diamond is 0.858 of
    the box wide; standing on a point, it reaches 0.5515 of the box either side
    of the centre.
  */
  const DIE_SQUARE = 0.858;
  const DIE_POINT = 0.5515;
  const DIE_CLEARANCE = 5.5; // 5px, plus half a pixel for the antialiased edge

  const pickCard = (selector, pick) => {
    const found = Array.from(document.querySelectorAll(selector)).filter((el) => el.offsetParent);
    return pick === 'last' ? found[found.length - 1] : found[0];
  };

  /*
    Hannah, 5 Oct (KAN-811): the die lands on top of the product card. It comes
    to rest standing on the top edge of the last card in its board's first row,
    lined up with the card's right edge, at least DIE_CLEARANCE px from the
    track; where that space is narrower than the die, the die is drawn smaller.
  */
  const placeOnCard = (dice) => {
    const board = dice.offsetParent;
    if (!board) return;
    const cards = Array.from(board.querySelectorAll('.gg-card')).filter((card) => card.offsetParent);
    if (!cards.length) return;
    const top = Math.min(...cards.map((card) => card.getBoundingClientRect().top));
    const card = cards
      .map((el) => el.getBoundingClientRect())
      .filter((box) => Math.abs(box.top - top) < 1)
      .sort((a, b) => b.right - a.right)[0];
    dice.style.width = dice.style.height = '';
    const bottom = card.top;
    let side = dice.offsetWidth * DIE_SQUARE;
    Array.from(board.querySelectorAll('.gg-track')).forEach((track) => {
      const box = track.getBoundingClientRect();
      if (!box.width || box.bottom <= bottom - side || box.top >= bottom || box.right > card.right) return;
      side = Math.min(side, card.right - box.right - DIE_CLEARANCE);
    });
    const size = side / DIE_SQUARE;
    if (size < dice.offsetWidth) dice.style.width = dice.style.height = `${size}px`;
    const left = card.right - side;
    const base = board.getBoundingClientRect();
    const inset = (size - side) / 2;
    dice.style.left = `${left - inset - base.left - board.clientLeft}px`;
    dice.style.top = `${bottom - side - inset - base.top - board.clientTop}px`;
  };

  // The angle a dice rests at: the end of its roll, or its block setting.
  const restAngle = (dice) => {
    const path = DICE_PATHS[dice.dataset.ggMotion];
    if (path && !reduceMotion.matches) return path.rotate[path.rotate.length - 1][1];
    return (parseFloat(dice.style.getPropertyValue('--gg-dice-rotate')) || 0) * Math.PI / 180;
  };

  // The die's outline, as points around the centre of its box (px, unturned).
  const outline = (dice) => {
    const shape = dice.querySelector('svg path, svg rect');
    if (!shape) return [];
    const view = shape.ownerSVGElement.viewBox.baseVal;
    const unit = dice.offsetWidth / (view && view.width ? view.width : dice.offsetWidth);
    const matrix = shape.transform && shape.transform.baseVal.consolidate();
    const length = shape.getTotalLength ? shape.getTotalLength() : 0;
    const points = length
      ? Array.from({ length: 96 }, (_, i) => shape.getPointAtLength((i / 96) * length))
      : [[0, 0], [view.width, 0], [0, view.height], [view.width, view.height]].map(([x, y]) => ({ x, y }));
    const half = dice.offsetWidth / 2;
    return points.map((point) => {
      const q = matrix ? point.matrixTransform(matrix.matrix) : point;
      return [q.x * unit - half, q.y * unit - half];
    });
  };

  // How far the drawing reaches from the centre of its box when turned to `angle`.
  const reach = (points, angle) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const xs = points.map(([x, y]) => x * cos - y * sin);
    const ys = points.map(([x, y]) => x * sin + y * cos);
    return { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
  };

  // The centre of a dice's box, in viewport pixels.
  const centreOf = (dice) => {
    const board = dice.offsetParent;
    const base = board.getBoundingClientRect();
    const half = dice.offsetWidth / 2;
    return { x: base.left + board.clientLeft + dice.offsetLeft + half, y: base.top + board.clientTop + dice.offsetTop + half };
  };

  const moveCentreTo = (dice, x, y) => {
    const now = centreOf(dice);
    dice.style.left = `${dice.offsetLeft + x - now.x}px`;
    dice.style.top = `${dice.offsetTop + y - now.y}px`;
  };

  // The shortest moves that take a drawing at least DIE_CLEARANCE px from each box;
  // a second pass settles a corner where two boxes meet.
  const clearOf = (drawn, boxes) => {
    let dx = 0;
    let dy = 0;
    for (let pass = 0; pass < 2; pass += 1) {
      boxes.forEach((box) => {
        const left = drawn.left + dx;
        const right = drawn.right + dx;
        const top = drawn.top + dy;
        const bottom = drawn.bottom + dy;
        if (Math.max(box.left - right, left - box.right) >= DIE_CLEARANCE
          || Math.max(box.top - bottom, top - box.bottom) >= DIE_CLEARANCE) return;
        const moves = [
          [box.right + DIE_CLEARANCE - left, 0], [box.left - DIE_CLEARANCE - right, 0],
          [0, box.bottom + DIE_CLEARANCE - top], [0, box.top - DIE_CLEARANCE - bottom],
        ].sort((a, b) => Math.abs(a[0] + a[1]) - Math.abs(b[0] + b[1]));
        dx += moves[0][0];
        dy += moves[0][1];
      });
    }
    return { dx, dy };
  };

  const trackBoxes = () => Array.from(document.querySelectorAll('.gg .gg-track'))
    .filter((t) => getComputedStyle(t).visibility !== 'hidden')
    .map((t) => t.getBoundingClientRect()).filter((r) => r.width);

  // The badges at the ends of the bars (UNDER $50): the whole circle each
  // sweeps as it turns, whatever angle it is at now.
  const badgeBoxes = () => Array.from(document.querySelectorAll('.gg .gg-badge__spin')).filter(shown).map((el) => {
    const box = el.getBoundingClientRect();
    const x = (box.left + box.right) / 2;
    const y = (box.top + box.bottom) / 2;
    const r = Math.hypot(el.offsetWidth, el.offsetHeight) / 2;
    return { left: x - r, right: x + r, top: y - r, bottom: y + r };
  });

  const shown = (el) => el && el.offsetParent;

  // The sides of the screen, as boxes a die keeps clear of.
  const screenSides = () => [
    { left: -1e5, right: 0, top: -1e7, bottom: 1e7 },
    { left: document.documentElement.clientWidth, right: 1e5, top: -1e7, bottom: 1e7 },
  ];

  // The YES / NO boxes, including their outside ring.
  const choiceBoxes = () => Array.from(document.querySelectorAll('.gg-choice')).filter(shown).map((el) => {
    const box = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    const ring = style.outlineStyle === 'none' ? 0 : Math.max(0, parseFloat(style.outlineWidth) + parseFloat(style.outlineOffset));
    return { left: box.left - ring, right: box.right + ring, top: box.top - ring, bottom: box.bottom + ring };
  });

  const cardBoxes = () => Array.from(document.querySelectorAll('.gg .gg-card')).filter(shown).map((el) => el.getBoundingClientRect());

  /*
    Hannah, 5 Oct (KAN-811): 5px between icons. A dice whose drawing would rest
    closer than DIE_CLEARANCE px to a track moves the shortest way clear. The
    drawing is measured from the die's own outline turned to its resting angle,
    not from its square box.
  */
  const clearTrack = (dice) => {
    const drawn = reach(outline(dice), restAngle(dice));
    const c = centreOf(dice);
    const { dx, dy } = clearOf({ left: c.x + drawn.left, right: c.x + drawn.right, top: c.y + drawn.top, bottom: c.y + drawn.bottom }, trackBoxes());
    if (dx || dy) moveCentreTo(dice, c.x + dx, c.y + dy);
  };

  /*
    Cooper, 13 Oct (KAN-811): the phone dice land like the desktop ones, at the
    end of the Figma prototype's roll. The two long rolls stand on top of the
    Bundles bar, DIE_CLEARANCE px right of its stem; the Under $50 die stands on
    the last card of the first line, DIE_CLEARANCE px left of the track's leg.
  */
  const landOnBar = (dice) => {
    const board = dice.offsetParent;
    const bar = Array.from(board.querySelectorAll('.gg-bar')).find(shown);
    const stem = Array.from(board.querySelectorAll('.gg-stem')).find(shown);
    if (!bar || !stem) { clearTrack(dice); return; }
    const drawn = reach(outline(dice), restAngle(dice));
    moveCentreTo(dice, stem.getBoundingClientRect().right + DIE_CLEARANCE - drawn.left, bar.getBoundingClientRect().top - drawn.bottom);
  };

  const landByLeg = (dice) => {
    const board = dice.offsetParent;
    const leg = Array.from(board.querySelectorAll('.gg-leg--right')).find(shown);
    const cards = Array.from(board.querySelectorAll('.gg-card')).filter(shown).map((card) => card.getBoundingClientRect());
    if (!leg || !cards.length) { clearTrack(dice); return; }
    const top = Math.min(...cards.map((box) => box.top));
    const card = cards.filter((box) => Math.abs(box.top - top) < 1).sort((a, b) => b.right - a.right)[0];
    const drawn = reach(outline(dice), restAngle(dice));
    moveCentreTo(dice, leg.getBoundingClientRect().left - DIE_CLEARANCE - drawn.right, card.top - drawn.bottom);
  };

  /*
    Where each dice rests, before its roll is laid out. Returns how far its
    centre moved from the spot its block setting gives it, so a roll can still
    start where the Figma starts it.
  */
  const placeDice = (dice) => {
    if (!dice.offsetParent) return { x: 0, y: 0 };
    dice.style.left = dice.style.top = dice.style.width = dice.style.height = '';
    // A phone roll that cannot keep clear at full size is drawn smaller (see
    // layoutRoll), about its own centre.
    const scale = parseFloat(dice.dataset.ggScale) || 1;
    if (scale < 1) {
      const full = dice.offsetWidth;
      const left = dice.offsetLeft;
      const top = dice.offsetTop;
      dice.style.width = dice.style.height = `${full * scale}px`;
      dice.style.left = `${left + (full * (1 - scale)) / 2}px`;
      dice.style.top = `${top + (full * (1 - scale)) / 2}px`;
    }
    const before = centreOf(dice);
    const rest = DICE_PATHS[dice.dataset.ggMotion]?.rest;
    if (rest === 'on-card') placeOnCard(dice);
    else if (rest === 'on-bar') landOnBar(dice);
    else if (rest === 'by-leg') landByLeg(dice);
    else clearTrack(dice);
    const after = centreOf(dice);
    return { x: after.x - before.x, y: after.y - before.y };
  };

  /*
    Preview only: the roll's path as the Figma prototype draws it ('figma'),
    which passes over cards in places, or bent to keep DIE_CLEARANCE px from
    every card and the YES / NO boxes ('clear', the default).
  */
  const dicePathMode = () => (root.dataset.ggDicePath === 'figma' ? 'figma' : 'clear');
  // The board the desktop dice roll across, at the Figma's 1440px width.
  const FIGMA_BOARD = 1370;

  // Moves to try for a point of a roll, nearest first (4px steps, up to 140px).
  const NUDGES = (() => {
    const list = [];
    for (let dx = -140; dx <= 140; dx += 4) for (let dy = -140; dy <= 140; dy += 4) list.push([dx, dy]);
    return list.sort((a, b) => Math.hypot(a[0], a[1]) - Math.hypot(b[0], b[1]));
  })();

  /*
    Bend a roll, smoothly and only where needed, so the drawn die keeps
    DIE_CLEARANCE px (and room for the move and turn between two sampled points) from
    every product card, the YES / NO boxes and the sides of the screen. It may
    roll across the track, drawn over it: the track is the board it rolls on,
    and the phone Yes roll has to cross the YES stem, which runs unbroken from
    the YES box to the card. Each point that is too close takes the smallest move, in any
    direction, that clears everything at once; the moves are then spread
    smoothly along the path and checked again. The card a desktop die lands on
    may be touched but not crossed (3px of room covers the turn between points). The last 1% of the roll (the landing) is
    left as it is, and so is the start when `keepStart` is set. `extra` adds
    boxes to keep clear of (the Under $50 roll's bar, leg and badge).
  */
  const DIRECTIONS = Array.from({ length: 16 }, (_, k) => [Math.cos((k * Math.PI) / 8), Math.sin((k * Math.PI) / 8)]);
  const avoidCards = (samples, centre, points, keepStart = false, extra = [], sidesOnly = false) => {
    const restReach = reach(points, samples[samples.length - 1].rad);
    const restBottom = centre.y + restReach.bottom;
    const landsOn = (b) => Math.abs(b.top - restBottom) < 1.5
      && b.left < centre.x + restReach.right && b.right > centre.x + restReach.left;
    // The die is drawn between two sampled points too: allow half the longest
    // step between them, and take each point's reach over the turn to its
    // neighbours.
    const step = Math.max(0, ...samples.slice(1).map((s, i) => Math.hypot(s.x - samples[i].x, s.y - samples[i].y)));
    const between = Math.max(2, step / 2 + 0.5);
    const boxes = (sidesOnly ? screenSides() : cardBoxes().concat(choiceBoxes(), screenSides(), extra))
      .map((b) => ({ left: b.left, right: b.right, top: b.top, bottom: b.bottom, room: landsOn(b) ? 3 : DIE_CLEARANCE + between }));
    const reaches = samples.map((s, i) => [s.rad,
      (s.rad + samples[Math.max(0, i - 1)].rad) / 2,
      (s.rad + samples[Math.min(samples.length - 1, i + 1)].rad) / 2]
      .map((rad) => reach(points, rad))
      .reduce((a, b) => ({ left: Math.min(a.left, b.left), right: Math.max(a.right, b.right), top: Math.min(a.top, b.top), bottom: Math.max(a.bottom, b.bottom) })));
    const fixed = samples.map((s) => s.p > 0.99 || (keepStart && s.p < 0.01));
    const clearAt = (i, dx, dy) => {
      const s = samples[i];
      const r = reaches[i];
      const left = centre.x + s.x + dx + r.left;
      const right = centre.x + s.x + dx + r.right;
      const top = centre.y + s.y + dy + r.top;
      const bottom = centre.y + s.y + dy + r.bottom;
      return boxes.every((b) => right + b.room <= b.left || left - b.room >= b.right
        || bottom + b.room <= b.top || top - b.room >= b.bottom);
    };
    /*
      Each stretch of the roll that runs too close moves around the obstacle as
      one: every point of the stretch moves the same way (one of 16 directions),
      each as far as it needs, choosing the direction that needs the least.
      Moving each point on its own let neighbours land on opposite sides of a
      card, so the die jumped through it between two frames. The moves are then
      smoothed by averaging neighbours wherever that stays clear; repeat, and
      finish with a last pass so every point is clear.
    */
    const move = samples.map(() => [0, 0]);
    const along = (i, [ux, uy], limit) => {
      for (let t = 1; t <= limit; t += 1) {
        if (clearAt(i, move[i][0] + ux * t, move[i][1] + uy * t)) return t;
      }
      return null;
    };
    const project = () => {
      let moved = false;
      for (let i = 0; i < move.length; i += 1) {
        if (fixed[i] || clearAt(i, move[i][0], move[i][1])) continue;
        let j = i;
        while (j + 1 < move.length && !fixed[j + 1] && !clearAt(j + 1, move[j + 1][0], move[j + 1][1])) j += 1;
        let best = null;
        DIRECTIONS.forEach((dir) => {
          const needs = [];
          for (let k = i; k <= j; k += 1) {
            const t = along(k, dir, best ? best.cost : 240);
            if (t === null) return;
            needs.push(t);
          }
          const cost = Math.max(...needs);
          if (!best || cost < best.cost) best = { cost, dir, needs };
        });
        for (let k = i; k <= j; k += 1) {
          if (best) {
            move[k] = [move[k][0] + best.dir[0] * best.needs[k - i], move[k][1] + best.dir[1] * best.needs[k - i]];
          } else {
            const found = NUDGES.find(([ox, oy]) => clearAt(k, move[k][0] + ox, move[k][1] + oy));
            if (found) move[k] = [move[k][0] + found[0], move[k][1] + found[1]];
          }
        }
        moved = true;
        i = j;
      }
      return moved;
    };
    for (let round = 0; round < 40; round += 1) {
      const moved = project();
      if (!moved && round > 0 && !move.some((m) => m[0] || m[1])) break;
      for (let i = 1; i < move.length - 1; i += 1) {
        if (fixed[i]) continue;
        const x = (move[i - 1][0] + 2 * move[i][0] + move[i + 1][0]) / 4;
        const y = (move[i - 1][1] + 2 * move[i][1] + move[i + 1][1]) / 4;
        if (clearAt(i, x, y)) move[i] = [x, y];
      }
    }
    project();
    const out = samples.map((s, i) => ({ ...s, x: s.x + move[i][0], y: s.y + move[i][1] }));
    // Clear at every point, and no step between two points much longer than
    // the Figma path's own (a longer one would skip through something).
    const longest = Math.max(0, ...out.slice(1).map((s, i) => Math.hypot(s.x - out[i].x, s.y - out[i].y)));
    out.clear = move.every((m, i) => fixed[i] || clearAt(i, m[0], m[1])) && longest <= Math.max(12, step * 2);
    return out;
  };

  const anchorTop = (anchor) => {
    if (!anchor) return null;
    const found = Array.from(document.querySelectorAll(anchor.selector)).filter((el) => el.offsetParent);
    const el = anchor.pick === 'last' ? found[found.length - 1] : found[0];
    return el ? el.getBoundingClientRect().top + window.scrollY : null;
  };

  // Where a scroll-linked roll starts and ends, and its keyframes for that layout.
  const layoutRoll = (d) => {
    const board = d.dice.offsetParent;
    if (!board) {
      d.range = null; // hidden for this answer or screen size
      return;
    }
    const moved = placeDice(d.dice);
    const restTop = board.getBoundingClientRect().top + window.scrollY + d.dice.offsetTop;
    const tablet = window.matchMedia('(min-width: 750px) and (max-width: 1399px)').matches;
    /*
      Tablets have no Figma design, and the 1440px roll squeezed into them
      crosses the track, its headings and the cards (Cooper, 13 Oct). There the
      die follows the same path narrowed in proportion to the board, then keeps
      clear as usual.
    */
    const scaleX = tablet && d.dice.classList.contains('gg-dice--desktop') ? Math.min(1, board.clientWidth / FIGMA_BOARD) : 1;
    const anchor = anchorTop(d.path.anchor);
    const lift = anchor === null ? d.figmaLift : anchor + d.path.anchor.dy - restTop;
    const scaleY = d.figmaLift ? lift / d.figmaLift : 1;
    // The roll runs while the middle of the screen passes from its start to its
    // rest spot (or, with `travel`, while the rest spot rises between two points
    // of the screen).
    const travel = d.path.travel;
    const begin = travel ? restTop - window.innerHeight * travel[0] : restTop + lift - window.innerHeight / 2;
    const end = travel ? restTop - window.innerHeight * travel[1] : restTop - window.innerHeight / 2;
    d.range = { begin, end };
    let samples = d.sample(scaleY, scaleX);
    let startShift = 0;
    let fit = { clear: true, startShift: 0 };
    const edge = d.path.edge && pickCard(d.path.edge.selector, d.path.edge.pick);
    if (edge) {
      // Ease the start sideways (fading to nothing at the rest spot) so the die,
      // standing on a point, just touches the card's right edge.
      const size = d.dice.offsetWidth;
      const centre = board.getBoundingClientRect().left + board.clientLeft + d.dice.offsetLeft + size / 2;
      const shift = edge.getBoundingClientRect().right + size * DIE_POINT - (centre + samples[0].x);
      samples = samples.map((s) => ({ ...s, x: s.x + shift * (1 - s.p) }));
    } else {
      // The rest spot moved (landing, 5px rule); the roll still starts where the
      // Figma starts it, then keeps DIE_CLEARANCE px from the track and the YES /
      // NO boxes there.
      samples = samples.map((s) => ({ ...s, x: s.x - moved.x * (1 - s.p) }));
      const points = outline(d.dice);
      const centre = centreOf(d.dice);
      const r = reach(points, samples[0].rad);
      const x0 = centre.x + samples[0].x;
      const y0 = centre.y + samples[0].y;
      const boxes = trackBoxes().concat(cardBoxes(), choiceBoxes(), screenSides(), d.path.avoidTrack ? badgeBoxes() : []);
      // The nearest spot that clears everything at once (beside the NO box where
      // there is room, below the boxes on the narrowest phones).
      const fits = ([ox, oy]) => boxes.every((b) => x0 + ox + r.right + DIE_CLEARANCE <= b.left
        || x0 + ox + r.left - DIE_CLEARANCE >= b.right
        || y0 + oy + r.bottom + DIE_CLEARANCE <= b.top || y0 + oy + r.top - DIE_CLEARANCE >= b.bottom);
      // Nearest first at 1px steps close by, then the wider 4px search.
      const fine = [];
      for (let ox = -12; ox <= 12; ox += 1) for (let oy = -12; oy <= 12; oy += 1) fine.push([ox, oy]);
      fine.sort((m, n) => Math.hypot(m[0], m[1]) - Math.hypot(n[0], n[1]));
      const [dx, dy] = fine.find(fits) || NUDGES.find(fits) || [0, 0];
      startShift = Math.hypot(dx, dy);
      if (dx || dy) samples = samples.map((s) => ({ ...s, x: s.x + dx * (1 - s.p), y: s.y + dy * (1 - s.p) }));
    }
    if (dicePathMode() === 'clear') {
      const extra = d.path.avoidTrack ? trackBoxes().concat(badgeBoxes()) : [];
      const path = samples;
      samples = avoidCards(path, centreOf(d.dice), outline(d.dice), Boolean(edge), extra);
      /*
        Below about 1280px the cards fill the board's width, so a fitted tablet
        roll has no way down that keeps 5px from them. There it follows the
        narrowed path across the cards, as the Figma's own path does on
        computers, kept only on screen.
      */
      if (!samples.clear && scaleX < 1) samples = avoidCards(path, centreOf(d.dice), outline(d.dice), Boolean(edge), [], true);
      fit = { clear: samples.clear, startShift };
    }
    const progressAt = (scroll) => Math.min(1, Math.max(0, (scroll - begin) / (end - begin || 1)));
    const at = (p) => {
      const n = p * (samples.length - 1);
      const a = samples[Math.floor(n)];
      const b = samples[Math.ceil(n)];
      const u = n - Math.floor(n);
      return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, rad: a.rad + (b.rad - a.rad) * u };
    };
    let frames;
    if (d.timeline) {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const inside = samples
        .map((s) => ({ ...s, offset: (begin + s.p * (end - begin)) / max }))
        .filter((s) => s.offset > 0 && s.offset < 1);
      frames = [{ offset: 0, ...at(progressAt(0)) }, ...inside, { offset: 1, ...at(progressAt(max)) }];
    } else {
      frames = samples.map((s) => ({ offset: s.p, ...s }));
    }
    d.moves.effect.setKeyframes(frames.map((f) => ({
      offset: f.offset,
      transform: `translate(${f.x}px, ${f.y}px) rotate(${f.rad}rad)`,
    })));
    return fit;
  };

  /*
    On the narrowest phones a full-size die cannot keep DIE_CLEARANCE px on both
    sides beside the Reorder card, or start beside the NO box. A phone roll is
    laid out at full size, then 5% smaller at a time down to 70%, and keeps the
    largest size whose whole roll keeps clear (and, for the Yes die, starts
    within `startWithin` px of the Figma's spot, beside the NO box); failing
    that, the largest size that keeps clear.
  */
  const SCALES = [1, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7];
  const fitRoll = (d) => {
    delete d.dice.dataset.ggScale;
    if (!d.dice.classList.contains('gg-dice--mobile') || dicePathMode() !== 'clear') {
      layoutRoll(d);
      return;
    }
    let clearScale = null;
    let tried = null;
    for (const scale of SCALES) {
      d.dice.dataset.ggScale = String(scale);
      const fit = layoutRoll(d);
      tried = scale;
      if (!fit) return; // hidden for this answer or screen size
      if (fit.clear && fit.startShift <= (d.path.startWithin ?? Infinity)) return;
      if (fit.clear && clearScale === null) clearScale = scale;
    }
    const keep = clearScale === null ? 1 : clearScale;
    if (keep !== tried) {
      d.dice.dataset.ggScale = String(keep);
      layoutRoll(d);
    }
  };

  // The theme editor re-runs initDice when a section reloads.
  let stopDice = () => {};

  const initDice = () => {
    stopDice();
    if (reduceMotion.matches) {
      // No rolls: the dice are drawn at their rest spots.
      const place = () => document.querySelectorAll('[data-gg-dice]').forEach(placeDice);
      place();
      // Images and fonts move the cards as they load.
      const sizes = 'ResizeObserver' in window ? new ResizeObserver(place) : null;
      if (sizes) sizes.observe(document.body);
      window.addEventListener('resize', place);
      document.addEventListener('gift-guide:path', place);
      stopDice = () => {
        if (sizes) sizes.disconnect();
        window.removeEventListener('resize', place);
        document.removeEventListener('gift-guide:path', place);
      };
      return;
    }
    const all = Array.from(document.querySelectorAll('[data-gg-dice]'))
      .map(buildDice)
      .filter(Boolean);

    const rolling = all.filter((d) => d.path.trigger === 'scroll');
    // Without scroll timelines, follow the scroll here (reads no layout per frame).
    const follow = () => {
      rolling.forEach((d) => {
        if (d.timeline || !d.range) return;
        const { begin, end } = d.range;
        const progress = Math.min(1, Math.max(0, (window.scrollY - begin) / (end - begin || 1)));
        d.moves.currentTime = progress * 1000;
      });
    };
    const relayout = () => {
      document.querySelectorAll('[data-gg-dice]').forEach((dice) => {
        if (rolling.some((d) => d.dice === dice)) return;
        const moved = placeDice(dice);
        const d = all.find((item) => item.dice === dice);
        if (!d || !d.turns) return;
        // A roll played in time starts where the Figma starts it, wherever it lands.
        const [, endX, endY] = d.path.translate[d.path.translate.length - 1];
        const last = d.path.window[1];
        d.moves.effect.setKeyframes(d.path.translate.map(([pct, x, y]) => {
          const left = 1 - Math.min(1, pct / last);
          return { offset: pct / 100, transform: `translate(${x - endX - moved.x * left}px, ${y - endY - moved.y * left}px)` };
        }));
      });
      rolling.forEach(fitRoll);
      follow();
    };

    // One task per frame; a relayout also covers a pending scroll update.
    let queued = null;
    const queue = (task) => () => {
      if (queued === relayout || queued === task) return;
      const pending = queued;
      queued = task;
      if (pending) return;
      requestAnimationFrame(() => {
        const run = queued;
        queued = null;
        run();
      });
    };
    const onScroll = queue(follow);
    const onLayout = queue(relayout);
    relayout();
    // Show the dice (hidden while data-gg-dice-pending is set) only once each
    // rolling one paints at its scroll position, so it never flashes at its
    // resting spot first; give up waiting after about half a second.
    let waits = 0;
    const reveal = () => {
      const placed = rolling.every((d) => !d.range || getComputedStyle(d.dice).transform !== 'none');
      if (!placed && waits++ < 30) { requestAnimationFrame(reveal); return; }
      document.querySelectorAll('[data-gg-dice]').forEach((dice) => dice.classList.add('is-placed'));
    };
    requestAnimationFrame(reveal);
    // Images, fonts and the Yes / No answer change where each roll starts and ends.
    const sizes = 'ResizeObserver' in window ? new ResizeObserver(onLayout) : null;
    if (sizes) sizes.observe(document.body);
    const needsScroll = rolling.some((d) => !d.timeline);
    if (needsScroll) window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onLayout);
    document.addEventListener('gift-guide:path', onLayout);
    stopDice = () => {
      if (sizes) sizes.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onLayout);
      document.removeEventListener('gift-guide:path', onLayout);
      all.forEach((d) => {
        d.moves.cancel();
        if (d.turns) d.turns.cancel();
        d.dice.classList.remove('is-rolling');
      });
    };

    onFirstView(
      all.filter((d) => d.path.trigger === 'view').map((d) => d.dice),
      (el) => {
        const d = all.find((item) => item.dice === el);
        d.moves.play();
        d.turns.play();
      },
      { rootMargin: '0px 0px -20% 0px' }
    );
  };

  const showError = (form, message) => {
    const slot = form.querySelector('[data-gg-error]');
    if (slot) slot.textContent = message;
  };

  const validate = (form) => {
    let firstInvalid = null;
    form.querySelectorAll('[data-gg-required]').forEach((input) => {
      input.value = input.value.trim();
      const missing = input.value === '';
      input.setAttribute('aria-invalid', String(missing));
      if (missing && !firstInvalid) firstInvalid = input;
    });
    if (firstInvalid) {
      showError(form, 'Please fill in both fields.');
      firstInvalid.focus();
      return false;
    }
    return true;
  };

  const cartItemCount = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      return typeof cart.item_count === 'number' ? cart.item_count : null;
    } catch (error) {
      return null;
    }
  };

  // Refreshes and opens the slide-out cart without the add response (used when
  // another script's fetch wrapper threw on a successful add). Returns false
  // when there is no drawer to open.
  const openDrawerAfterAdd = async (drawer, variantId) => {
    if (!drawer || typeof drawer.renderContents !== 'function' || typeof drawer.getSectionsToRender !== 'function') return false;
    try {
      const ids = drawer.getSectionsToRender().map((section) => section.section);
      const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      const sections = await response.json();
      drawer.renderContents({ id: Number(variantId), sections });
      return true;
    } catch (error) {
      return false;
    }
  };

  const addToCart = async (form) => {
    const button = form.querySelector('button[type="submit"]');
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const variantSelect = form.querySelector('[data-gg-variant]');
    if (variantSelect && (!variantSelect.value || variantSelect.selectedOptions[0].disabled)) return;
    showError(form, '');
    if (!validate(form)) return;

    const body = new FormData(form);
    // Match the 50ml customizer, which stores both names in capitals.
    form.querySelectorAll('[data-gg-required]').forEach((input) => {
      body.set(input.name, input.value.toUpperCase());
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.append('sections', drawer.getSectionsToRender().map((section) => section.section));
      body.append('sections_url', window.location.pathname);
    }

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (variantSelect) {
      variantSelect.disabled = true;
      variantSelect.dispatchEvent(new Event('gg:sync'));
    }
    const countBefore = await cartItemCount();
    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        showError(form, data.description || data.message || 'Sorry, that could not be added to your cart.');
        return;
      }
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      // Another script on the storefront wraps fetch and can throw on a
      // successful add, so check the cart before reporting a failure.
      console.error('[gift-guide] add to cart', error);
      const countAfter = await cartItemCount();
      if (countBefore !== null && countAfter !== null && countAfter > countBefore) {
        // It was added: open the slide-out cart, as a successful add does.
        if (!(await openDrawerAfterAdd(drawer, body.get('id')))) window.location.href = '/cart';
        return;
      }
      showError(form, 'Sorry, that could not be added to your cart. Please try again.');
    } finally {
      button.removeAttribute('aria-busy');
      if (variantSelect) {
        variantSelect.disabled = false;
        variantSelect.dispatchEvent(new Event('gg:sync'));
      }
      button.disabled = variantSelect ? !variantSelect.value || variantSelect.selectedOptions[0].disabled : false;
    }
  };

  const initListeners = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.gg [data-gg-choice]');
      if (button) {
        setPath(button.dataset.ggChoice, { animate: true });
        return;
      }
      /*
        A switch strip's button (sections/gift-guide-switch.liquid) answers the
        question for the shopper, then takes them back up to it, where the
        answer now shows as pressed and its board starts (Hannah, 10/14).
      */
      const switcher = event.target.closest('.gg [data-gg-switch-to]');
      if (!switcher) return;
      const to = switcher.dataset.ggSwitchTo;
      setPath(to, { animate: true });
      const question = document.querySelector('[data-gg-question]');
      const target = question || document.querySelector('.gg');
      if (target) {
        const header = document.querySelector('.section--header');
        const offset = header ? header.getBoundingClientRect().height : 0;
        const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      const pressed = document.querySelector(`.gg [data-gg-choice="${to}"]`);
      if (pressed) pressed.focus({ preventScroll: true });
    });
    /*
      Caught on the way down (capture, on window) and stopped there: the
      EasyGift gift-with-purchase app listens for submits of any form posting
      to /cart/add and adds the item itself, so letting the event reach it
      added every gift guide item twice (Hannah, 10/15).
    */
    window.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-gg-add]');
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addToCart(form);
    }, true);
    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-gg-variant]');
      if (!select) return;
      const form = select.closest('form');
      const option = select.selectedOptions[0];
      form.querySelector('button[type="submit"]').disabled = !option || option.disabled;
      form.querySelectorAll('[data-gg-variant-price]').forEach((price) => { price.textContent = option?.dataset.price || ''; });
      showError(form, '');
    });
    document.addEventListener('input', (event) => {
      const input = event.target.closest('[data-gg-add] [data-gg-required]');
      if (input && input.value.trim() !== '') input.setAttribute('aria-invalid', 'false');
    });
  };

  const init = () => {
    initQuestion();
    initReveal();
    initVariantPickers();
    initPreviewStates();
    initDice();
    initListeners();
    reduceMotion.addEventListener('change', () => {
      setReveal(root.dataset.ggReveal);
      initDice();
    });
    touchOnly.addEventListener('change', () => setReveal(root.dataset.ggReveal));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when a gift-guide section is added or edited.
  document.addEventListener('shopify:section:load', () => {
    initQuestion();
    initReveal();
    initVariantPickers();
    initPreviewStates();
    initDice();
  });
})();
